import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { processAndNotify } from '@/components/plugins/GlobalDiceRoller/criticalNotifierService'
import DiceAnalyticsService from '@/services/dice/DiceAnalyticsService'
import { initDatabase, saveToDb } from '@/utils/indexedDBHelper'
import { useUserStore } from '@/stores/userStore'

// Helper function
function generateUniqueId() {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 9)
}

// --- Dice Appearance Persistence ---
// We no longer use localStorage for persistence. Defaults are in-memory
// and the enhanced dice store will apply persisted IndexedDB values on init.
const initialDiceAppearance = {
  activeDiceSkin: 'default',
  diceColor: '#ffffff', // fallback default
  letterColor: '#000000' // fallback default
}

// --- Dice 3D Mode Persistence (separate from appearance) ---
// No localStorage; use default and let the enhanced store apply persisted IDB values
const initialUse3DMode = true

// --- Dice Sound Option Persistence ---
const initialPlayDiceSound = true // Default to true

/**
 * Store for managing the global dice roller
 */
export const useDiceRollerStore = defineStore('diceRoller', () => {
  // State
  const isVisible = ref(false)
  const overlayOpacity = ref(0.8)
  const activeDiceSkin = ref(initialDiceAppearance.activeDiceSkin)
  const diceColor = ref(initialDiceAppearance.diceColor)
  const letterColor = ref(initialDiceAppearance.letterColor)
  const use3DMode = ref(initialUse3DMode)
  const playDiceSound = ref(initialPlayDiceSound)
  const diceToRoll = ref([]) // Stores configs like [{ type: 'd20', count: 1, modifier: 0 }]
  const rollHistory = ref([])

  // Hydrate roll history from localStorage (best-effort, capped to 50)
  try {
    const rawHistory = localStorage.getItem('diceRoller.history')
    if (rawHistory) {
      const parsed = JSON.parse(rawHistory)
      if (Array.isArray(parsed)) {
        rollHistory.value = parsed.slice(-50)
      }
    }
  } catch {
    /* ignore hydration errors */
  }
  const activeRoll = ref(null) // Manages the currently executing physical roll
  // Available dice skins (only image/material-based, e.g., marble, obsidian)
  const availableSkins = ref([
    {
      name: 'Default',
      value: 'default',
      image: null,
      materialProps: { roughness: 0.7, metalness: 0.1, opacity: 1, transmission: 0, ior: 1.5 }
    },
    {
      name: 'Marble',
      value: 'marble',
      image: '/images/dice-roller/marble.avif',
      materialProps: { roughness: 0.5, metalness: 0.2, opacity: 1, transmission: 0, ior: 1.5 }
    },
    {
      name: 'Obsidian',
      value: 'obsidian',
      image: '/images/dice-roller/obsidian.jpg',
      materialProps: { roughness: 0.2, metalness: 0.2, opacity: 1, transmission: 0, ior: 0 }
    },
    {
      name: 'Ivory',
      value: 'ivory',
      image: '/images/dice-roller/ivory.jpg',
      materialProps: { roughness: 0.6, metalness: 0.1, opacity: 1, transmission: 0, ior: 1.6 }
    },
    {
      name: 'Wood',
      value: 'wood',
      image: '/images/dice-roller/wood.webp',
      materialProps: { roughness: 0.8, metalness: 0.05, opacity: 1, transmission: 0, ior: 1.45 }
    },
    {
      name: 'Glass',
      value: 'glass',
      image: null,
      materialProps: {
        roughness: 0.05,
        metalness: 0.0,
        opacity: 1.0,
        transmission: 1.0,
        ior: 1.45,
        transparent: true,
        thickness: 1.5,
        clearcoat: 1.0,
        clearcoatRoughness: 0.03,
        reflectivity: 0.6,
        envMapIntensity: 2.0,
        specularIntensity: 1.0,
        attenuationDistance: 0.6
        // attenuationColor will be set at runtime in BaseDie
      }
    }
    // Add more material/image-based skins here
  ])

  // All available dice types
  const allDiceTypes = [
    { name: 'd4', value: 'd4' },
    { name: 'd6', value: 'd6' },
    { name: 'd8', value: 'd8' },
    { name: 'd10', value: 'd10' },
    { name: 'd12', value: 'd12' },
    { name: 'd20', value: 'd20' },
    { name: 'd100', value: 'd100' } // Percentile die
  ]

  // Getters
  const isDiceBoxEmpty = computed(() => diceToRoll.value.length === 0)
  const totalDiceCount = computed(() => {
    return diceToRoll.value.reduce((sum, die) => sum + (die.count || 0), 0)
  })
  // Get the full skin object by value or by object
  function getSkinByValue(skinValue) {
    if (!skinValue) return null
    if (typeof skinValue === 'object' && skinValue.value) return skinValue
    return availableSkins.value.find((s) => s.value === skinValue || s.name === skinValue) || null
  }
  // Get the full dice appearance (skin, diceColor, letterColor, material/image)
  function getDiceAppearance(skinValue = null, diceColorValue = null, letterColorValue = null) {
    // Use provided values or defaults from store
    const skinToUse = skinValue || activeDiceSkin.value
    const diceColorToUse = diceColorValue || diceColor.value
    const letterColorToUse = letterColorValue || letterColor.value
    const skinObj = getSkinByValue(skinToUse)
    return {
      skin: skinObj ? skinObj.value : typeof skinToUse === 'object' ? skinToUse.value : skinToUse,
      skinValue: skinObj
        ? skinObj.value
        : typeof skinToUse === 'object'
          ? skinToUse.value
          : skinToUse,
      diceColor: diceColorToUse,
      letterColor: letterColorToUse,
      material: skinObj && skinObj.image ? skinObj.image : null,
      skinObject: skinObj,
      materialProps: skinObj && skinObj.materialProps ? skinObj.materialProps : {}
    }
  }

  // Actions

  function showDiceRoller(options = {}) {
    isVisible.value = true

    if (options && Object.keys(options).length > 0) {
      // Do not overwrite an existing multi-dice pending config with a single-dice config
      const existingHasGroups =
        Array.isArray(pendingConfiguration.value?.initialDiceGroups) &&
        pendingConfiguration.value.initialDiceGroups.length > 0
      const incomingIsSingle = !!(options.initialDiceType && options.initialDiceCount)
      if (existingHasGroups && incomingIsSingle) {
        console.warn(
          'DiceRollerStore: Ignoring single-dice pending; multi-dice groups already pending'
        )
        return
      }
      pendingConfiguration.value = { ...options }
      console.log('DiceRollerStore: Stored pending configuration:', pendingConfiguration.value)
    }
  }

  // Add reactive refs for pending configuration that the overlay can watch
  const pendingConfiguration = ref(null)
  const pendingModifier = computed(() => pendingConfiguration.value?.initialModifier ?? null)
  const pendingDiceType = computed(() => pendingConfiguration.value?.initialDiceType ?? null)
  const pendingDiceCount = computed(() => pendingConfiguration.value?.initialDiceCount ?? null)
  const pendingDiceGroups = computed(() => {
    const groups = pendingConfiguration.value?.initialDiceGroups
    if (!Array.isArray(groups) || groups.length === 0) return null
    // normalize: ensure valid shape { type:'dX', count:n }
    return groups
      .map((g) => ({
        type: typeof g.type === 'string' ? g.type : null,
        count: Math.max(1, parseInt(g.count, 10) || 1)
      }))
      .filter((g) => !!g.type)
  })

  // Method to clear pending configuration (called by overlay watchers)
  function clearPendingConfiguration() {
    console.log('DiceRollerStore: Clearing pending configuration')
    pendingConfiguration.value = null
  }

  function hideDiceRoller() {
    isVisible.value = false
  }

  function toggleDiceRoller() {
    isVisible.value = !isVisible.value
  }
  function setDiceSkin(skin) {
    // Accepts either a skin object or a string value
    let selectedSkin = null
    if (typeof skin === 'object' && skin.value) {
      selectedSkin = skin
    } else {
      selectedSkin = availableSkins.value.find((s) => s.value === skin || s.name === skin)
    }
    if (selectedSkin) {
      activeDiceSkin.value = selectedSkin
      // Persist to IndexedDB (user-scoped)
      persistAppearanceToLocalDb().catch(() => {})
      return { ...selectedSkin }
    } else {
      console.warn(`Skin "${skin}" not found. Reverting to default.`)
      activeDiceSkin.value = availableSkins.value[0]
      return null
    }
  }

  function setOverlayOpacity(opacity) {
    overlayOpacity.value = Math.max(0, Math.min(1, opacity))
  }

  // Methods for 3D mode preference
  function setUse3DMode(value) {
    console.log('Setting 3D mode preference:', value)
    use3DMode.value = !!value // Convert to boolean
    // Persist to IndexedDB
    persistAppearanceToLocalDb().catch(() => {})
  }

  function getUse3DMode() {
    return use3DMode.value
  }

  // Methods for playDiceSound preference
  function setPlayDiceSound(value) {
    playDiceSound.value = !!value
    // Persist to IndexedDB
    persistAppearanceToLocalDb().catch(() => {})
  }

  function getPlayDiceSound() {
    return playDiceSound.value
  }

  // Add setter methods for colors that save to localStorage
  function setDiceColor(newColor) {
    diceColor.value = newColor
    // Persist to IndexedDB
    persistAppearanceToLocalDb().catch(() => {})
  }

  function setLetterColor(newColor) {
    letterColor.value = newColor
    // Persist to IndexedDB
    persistAppearanceToLocalDb().catch(() => {})
  }

  // Persist appearance/settings to IndexedDB under user-scoped key
  async function persistAppearanceToLocalDb() {
    try {
      const userStore = useUserStore()
      const uid = userStore.user?.uid
      if (!uid) return

      await initDatabase('dice_roller_db', 1, 'configs', null)

      const configToSave = {
        diceSkin:
          activeDiceSkin.value?.value ||
          (activeDiceSkin.value && activeDiceSkin.value.value) ||
          'default',
        diceColor: diceColor.value || '#ffffff',
        letterColor: letterColor.value || '#000000',
        playSound: playDiceSound.value ?? true,
        use3DMode: use3DMode.value ?? true
      }

      const cacheKey = `user_config_${uid}`
      await saveToDb('dice_roller_db', 'configs', cacheKey, 'user', uid, configToSave)
    } catch (e) {
      console.warn('[GlobalDiceRoller] Failed to persist appearance to IndexedDB', e)
    }
  }

  /**
   * Set the rolling state for the simplified dice implementation
   * @param {boolean} isRolling - Whether dice are currently rolling
   */
  function setRolling(isRolling) {
    // For the simplified implementation, we can use this to update UI state
    // The 3D implementation would use activeRoll directly
    if (!isRolling) {
      activeRoll.value = null // Clear active roll when done
    }
  }
  /**
   * Add a new dice configuration to the dice box (to be rolled)
   * @param {Object} diceConfig - The dice configuration object
   * @param {string} diceConfig.type - Type of die (d4, d6, etc.)
   * @param {number} diceConfig.count - Number of dice of this type
   * @param {number} [diceConfig.modifier=0] - Modifier for this specific dice type (usually 0, overall modifier handled by roll)
   */
  function addDice(diceConfig) {
    if (!diceConfig || !diceConfig.type) {
      console.warn('Invalid diceConfig passed to addDice:', diceConfig)
      return
    }

    const diceTypeString = typeof diceConfig.type === 'string' ? diceConfig.type : 'd20'
    if (!allDiceTypes.some((dt) => dt.value === diceTypeString)) {
      console.warn(`Invalid dice type: ${diceTypeString} in addDice, defaulting to d20`)
      diceToRoll.value = [...diceToRoll.value, { type: 'd20', count: 1, modifier: 0 }]
      return
    }

    // Check if this die type already exists in the collection
    const existingIndex = diceToRoll.value.findIndex((d) => d.type === diceTypeString)

    if (existingIndex !== -1) {
      // If die type already exists, update its count (properly triggering reactivity)
      const newCount = Math.min(
        Math.max(1, diceToRoll.value[existingIndex].count + (parseInt(diceConfig.count, 10) || 1)),
        20
      )

      // Create a new array with the updated die (preserving reactivity)
      diceToRoll.value = diceToRoll.value.map((die, index) =>
        index === existingIndex ? { ...die, count: newCount } : die
      )
    } else {
      // If new die type, add as a new object
      const normalizedConfig = {
        type: diceTypeString,
        count: Math.min(Math.max(1, parseInt(diceConfig.count, 10) || 1), 20),
        modifier: parseInt(diceConfig.modifier, 10) || 0 // Modifier here is per dice type, usually 0.
      }

      // Create a new array (preserving reactivity)
      diceToRoll.value = [...diceToRoll.value, normalizedConfig]
    }
  }

  function clearDice() {
    diceToRoll.value = []
  }
  function removeDieByIndex(index) {
    // Create new array without the die at the specified index
    const newDiceConfig = [...diceToRoll.value]

    // Find which config entry this die belongs to
    let currentDieCount = 0
    let configIndex = -1
    let diePositionInConfig = -1

    // Loop through dice configs to find which one contains this die
    for (let i = 0; i < newDiceConfig.length; i++) {
      const config = newDiceConfig[i]
      const configDiceCount = config.count || 1

      // Check if our target index is in this config's range
      if (index >= currentDieCount && index < currentDieCount + configDiceCount) {
        configIndex = i
        diePositionInConfig = index - currentDieCount
        break
      }

      currentDieCount += configDiceCount
    }

    // If we found the config
    if (configIndex !== -1) {
      // If there's only one die of this type, remove the whole config
      if (newDiceConfig[configIndex].count === 1) {
        // Create a new array without this configuration
        diceToRoll.value = diceToRoll.value.filter((_, idx) => idx !== configIndex)
      } else {
        // Otherwise, create a new array with updated count
        diceToRoll.value = diceToRoll.value.map((die, idx) =>
          idx === configIndex ? { ...die, count: die.count - 1 } : die
        )
      }
    }
  }

  /**
   * Add a roll to history
   * @param {Object} roll - The roll result, including notation, rawResults, modifier, total, and metadata
   */
  function addRollToHistory(roll) {
    const maxHistorySize = 50 // Increased history size
    if (rollHistory.value.length >= maxHistorySize) {
      rollHistory.value.shift()
    }
    const rollWithTimestamp = {
      ...roll,
      timestamp: new Date().toISOString()
    }
    rollHistory.value.push(rollWithTimestamp)

    // Persist to localStorage so silent rolls and refreshes retain history
    try {
      localStorage.setItem(
        'diceRoller.history',
        JSON.stringify(rollHistory.value.slice(-maxHistorySize))
      )
    } catch {
      /* ignore persistence errors */
    }
  }

  function clearRollHistory() {
    rollHistory.value = []
  }

  /**
   * Parses dice notation (e.g., "2d6+3", "d20", "1d8-1, 1d6") and adds to diceToRoll.
   * @param {string} notation - The dice notation string.
   * @returns {boolean} - True if parsing was successful and dice were added, false otherwise.
   */
  function addDiceFromNotation(notation) {
    if (!notation || typeof notation !== 'string') {
      console.warn('Invalid notation provided to addDiceFromNotation:', notation)
      return false
    }

    // Regex to parse complex notations like "2d6+3", "d20", "1d4-1"
    // It handles multiple dice groups separated by commas.
    const groupRegex = /([\s,]*)([\+-]?\s*\d*d\d+\s*(?:[\+\-]\s*\d+)?)/gi
    let match
    let diceAdded = false

    // Split by comma to handle multiple groups like "1d20+2, 1d6"
    const notations = notation
      .split(',')
      .map((s) => s.trim())
      .filter((s) => s.length > 0)

    if (notations.length === 0) {
      // Try a simpler parse if the complex one fails or for single dice like "d20"
      const simpleDieRegex = /(\d*)d(\d+)([\+\-]\d+)?/i
      const simpleMatch = notation.match(simpleDieRegex)
      if (simpleMatch) {
        notations.push(notation) // Reprocess as a single item
      } else {
        console.warn(`Could not parse notation: ${notation}`)
        return false
      }
    }

    for (const part of notations) {
      // Regex for individual dice part: (\d*)d(\d+)([\+\-]\d+)?
      // Group 1: Count (optional, defaults to 1)
      // Group 2: Type (e.g., 4, 6, 20)
      // Group 3: Modifier (optional, e.g., +5, -2)
      const dieRegex = /(\d*)d(\d+)\s*(?:([\+\-]\s*\d+))?/i
      const matches = part.match(dieRegex)

      if (matches) {
        const count = matches[1] ? parseInt(matches[1], 10) : 1
        const type = `d${matches[2]}`
        const modifier = matches[3] ? parseInt(matches[3].replace(/\s/g, ''), 10) : 0

        if (allDiceTypes.some((dt) => dt.value === type)) {
          addDice({ type, count, modifier }) // Modifier here is part of the notation for this group
          diceAdded = true
        } else {
          console.warn(`Invalid dice type in notation: ${type} from part ${part}`)
        }
      } else {
        console.warn(`Could not parse dice notation part: ${part}`)
      }
    }
    return diceAdded
  }

  /**
   * Perform the actual roll process.
   * This sets up the activeRoll state that the DiceRenderer will use.
   * @param {Object} [rollOptions=null] - Options for the roll, including notation and other metadata.
   *                                      Example: { notation: "2d6+3", note: "Attack roll", characterId: "char123" }
   */
  function performRoll(rollOptions = {}) {
    if (diceToRoll.value.length === 0) {
      console.warn('No dice to roll. Call addDice() or addDiceFromNotation() first.')
      return
    }

    const currentConfigs = [...diceToRoll.value] // Clone the current dice setup

    // Calculate total expected dice and overall modifier from the currentConfigs
    let expectedDiceCount = 0
    let baseModifier = 0 // Modifier from the dice notation itself (e.g. 2d6+3, the +3)

    currentConfigs.forEach((config) => {
      expectedDiceCount += config.count
      baseModifier += config.modifier || 0 // Sum up modifiers from notation parts
    })

    // Construct metadata
    let finalNotation = rollOptions.notation
    if (!finalNotation) {
      // Generate notation if not provided (e.g., for manually added dice)
      finalNotation = currentConfigs
        .map((cfg) => {
          let part = `${cfg.count}${cfg.type}`
          if (cfg.modifier && cfg.modifier !== 0) {
            part += (cfg.modifier > 0 ? '+' : '') + cfg.modifier
          }
          return part
        })
        .join(', ')
    }

    const metadata = {
      id: generateUniqueId(), // Unique ID for this specific roll instance
      notation: finalNotation,
      note: rollOptions.note || 'Manual Roll',
      characterId: rollOptions.characterId || null,
      rollType: rollOptions.rollType || null,
      // Include any other custom properties from rollOptions
      ...Object.fromEntries(
        Object.entries(rollOptions).filter(
          ([key]) => !['notation', 'note', 'characterId', 'rollType'].includes(key)
        )
      )
    }

    activeRoll.value = {
      diceConfigs: currentConfigs, // The specific dice to be thrown by the renderer
      metadata: metadata,
      expectedDiceCount: expectedDiceCount,
      totalBaseModifier: baseModifier, // Modifier derived from the notation (e.g. the +3 in 1d20+3)
      collectedResults: [] // Raw results from physical dice
    }

    // diceToRoll.value = []; // Clear the setup dice, as they are now part of activeRoll
    // Decision: Clearing diceToRoll here means the user has to set them up again for a re-roll.
    // If we want a "re-roll same dice" button, diceToRoll should persist.
    // For now, let's clear it, assuming rolls are generally unique setups.
    // This will be cleared by quickRoll or a manual clear button typically.
    // Let's defer clearing to quickRoll or a dedicated clearDice action.

    // The DiceRenderer component should now observe activeRoll
    // and create/throw the physical dice. Each die's onRollComplete
    // callback (passed during its creation by DiceRenderer)
    // should call store.reportDieSettled(resultValue).
  }

  /**
   * Called by the physics simulation (via DiceRenderer/BaseDie) when a single die has settled.
   * @param {number} rawValue - The face value of the settled die.
   * @param {string} dieId - An optional identifier for the die if needed for specific matching.
   */
  function reportDieSettled(rawValue, dieId = null) {
    // dieId might be useful later
    if (!activeRoll.value) {
      console.warn('Die settled but no active roll in progress. Ignoring result:', rawValue)
      return
    }

    activeRoll.value.collectedResults.push(rawValue)

    if (activeRoll.value.collectedResults.length >= activeRoll.value.expectedDiceCount) {
      // All dice for the current roll have settled
      const completedRollData = activeRoll.value
      const sumOfRawResults = completedRollData.collectedResults.reduce((sum, val) => sum + val, 0)
      const finalTotal = sumOfRawResults + completedRollData.totalBaseModifier

      const historyEntry = {
        // Core metadata
        notation: completedRollData.metadata.notation,
        note: completedRollData.metadata.note,
        characterId: completedRollData.metadata.characterId,
        rollType: completedRollData.metadata.rollType,

        // Raw results and calculation
        rawResults: [...completedRollData.collectedResults], // e.g., [5, 12, 3]
        modifier: completedRollData.totalBaseModifier, // e.g., +5
        total: finalTotal, // e.g., 25

        // Spread any other custom metadata properties
        ...Object.fromEntries(
          Object.entries(completedRollData.metadata).filter(
            ([key]) => !['id', 'notation', 'note', 'characterId', 'rollType'].includes(key)
          )
        )
      }
      // The unique 'id' from activeRoll.metadata.id is not typically stored in history,
      // as it was for tracking the active physical roll.

      // --- NOTIFIER HOOK ---
      // Always notify the notifier service after a completed roll, regardless of result.
      // Provide full roll info, types, raw results and the last two roll totals from history.
      try {
        // Gather last two roll totals from rollHistory (most recent first)
        const lastTwo = []
        for (let i = rollHistory.value.length - 1; i >= 0 && lastTwo.length < 2; i--) {
          const r = rollHistory.value[i]
          if (!r) continue
          // Prefer explicit total, otherwise fall back to first raw result
          const val =
            typeof r.total === 'number'
              ? r.total
              : Array.isArray(r.rawResults)
                ? Number(r.rawResults[0])
                : null
          if (val !== null && !Number.isNaN(val)) lastTwo.push(val)
        }

        // Build a rich metadata object describing the roll
        const richMetadata = {
          ...completedRollData.metadata,
          diceConfigs: completedRollData.diceConfigs,
          rawResults: [...completedRollData.collectedResults],
          total: finalTotal,
          modifier: completedRollData.totalBaseModifier
        }

        // Compose dieType summary (array of types or single string)
        const dieTypes = Array.isArray(completedRollData.diceConfigs)
          ? completedRollData.diceConfigs.map((c) => c.type)
          : completedRollData.diceConfigs

        // Call notifier asynchronously (non-blocking). The notifier will decide whether
        // to show anything (it may ignore non-critical rolls) but we still provide full context.
        try {
          if (
            typeof window !== 'undefined' &&
            window.__CRITICAL_NOTIFIER_DEBUG__ &&
            typeof console !== 'undefined' &&
            console.log
          )
            console.log('[CriticalNotifier] store calling processAndNotify', {
              finalTotal,
              dieTypes,
              lastTwo
            })
          processAndNotify({
            value: finalTotal,
            dieType: dieTypes,
            lastRolls: lastTwo,
            rollType: completedRollData.metadata.rollType,
            metadata: richMetadata
          })
            .then((res) => {
              if (
                typeof window !== 'undefined' &&
                window.__CRITICAL_NOTIFIER_DEBUG__ &&
                typeof console !== 'undefined' &&
                console.log
              )
                console.log('[CriticalNotifier] processAndNotify resolved', res)
            })
            .catch((err) => {
              if (
                typeof window !== 'undefined' &&
                window.__CRITICAL_NOTIFIER_DEBUG__ &&
                typeof console !== 'undefined' &&
                console.warn
              )
                console.warn('[CriticalNotifier] processAndNotify rejected', err)
            })
        } catch (e) {
          if (
            typeof window !== 'undefined' &&
            window.__CRITICAL_NOTIFIER_DEBUG__ &&
            typeof console !== 'undefined' &&
            console.warn
          )
            console.warn('Critical notifier invocation failed', e)
        }
      } catch (notifyErr) {
        console.warn('Critical notifier failed:', notifyErr)
      }

      addRollToHistory(historyEntry)
      activeRoll.value = null // Clear the active roll, ready for a new one
    }
  }

  // --- Quick Roll (grouped init) ---
  async function quickRoll(input, options = {}) {
    // Accepts string notation or { notation, modifier }
    const notation = typeof input === 'string' ? input : (input && input.notation) || ''
    const baseMod = Number.isFinite(options.modifier) ? Number(options.modifier) : 0

    // Parse expression into groups and flat modifier (e.g., 2d6+1d4-2)
    const s = String(notation || '')
      .replace(/\s+/g, '')
      .toLowerCase()
    const diceTerms = Array.from(s.matchAll(/([+-])?(\d*)d(\d+)/g))
    const flatTerms = Array.from(s.matchAll(/([+-])\s*(\d+)(?!d)/g))

    const groups = diceTerms
      .map((t) => {
        const cnt = t[2] ? parseInt(t[2], 10) || 1 : 1
        const sides = parseInt(t[3], 10) || 0
        return { type: sides > 0 ? `d${sides}` : null, count: Math.max(1, Math.abs(cnt)) }
      })
      .filter((g) => !!g.type)

    // Dedupe by type
    const byType = new Map()
    for (const g of groups) byType.set(g.type, (byType.get(g.type) || 0) + g.count)
    const dedupGroups = Array.from(byType.entries()).map(([type, count]) => ({
      type,
      count: Math.min(20, Math.max(1, count))
    }))

    const flat = flatTerms.reduce(
      (acc, m) => acc + (m[1] === '-' ? -1 : 1) * (parseInt(m[2], 10) || 0),
      0
    )
    const finalMod = baseMod + flat

    const shouldShowOverlay = options?.showOverlay !== false

    if (shouldShowOverlay) {
      // Initialize overlay with grouped config (no single-dice pending)
      if (typeof openWithGroups === 'function') {
        return openWithGroups(dedupGroups, finalMod)
      }
      // If openWithGroups not defined, fallback to setting pending groups directly
      pendingConfiguration.value = {
        initialDiceGroups: dedupGroups,
        ...(finalMod !== 0 ? { initialModifier: finalMod } : {})
      }
      isVisible.value = true
      return
    }

    // Silent roll path: roll instantly, log, and emit result without showing overlay
    const rawResults = dedupGroups.flatMap((g) => {
      const sides = parseInt(String(g.type).replace(/\D+/g, ''), 10) || 0
      const count = Math.max(1, parseInt(g.count, 10) || 1)
      return Array.from({ length: count }, () =>
        sides ? 1 + Math.floor(Math.random() * sides) : 0
      )
    })
    const total = rawResults.reduce((acc, v) => acc + v, 0) + finalMod
    const historyEntry = {
      notation: s,
      rawResults,
      modifier: finalMod,
      total,
      metadata: { ...options }
    }
    addRollToHistory(historyEntry)
    try {
      // Log to analytics so history dialog shows silent rolls (matches overlay behavior)
      await DiceAnalyticsService.logRoll({
        expression: s,
        result: total,
        individualRolls: rawResults.map((value) => ({ value })),
        modifier: finalMod,
        note: options?.note || '',
        rollType: options?.rollType || 'random',
        rollContext: options?.rollContext || 'general',
        characterId: options?.characterId || null,
        campaignId: options?.campaignId || null,
        sessionId: options?.sessionId || null,
        rollSource: options?.rollSource || 'ai_tool'
      })
    } catch (e) {
      console.warn('[DiceRoller] Failed to log silent roll to analytics', e)
    }
    try {
      if (typeof options?.onResult === 'function') options.onResult(historyEntry)
    } catch {
      /* ignore callback errors */
    }
    return historyEntry
  }

  // --- Character Sheet Integration (Example Roll Types) ---
  // These are convenience functions that use quickRoll with specific metadata

  function rollAbilityCheck(ability, characterId = null, bonus = 0) {
    const notation = `1d20${bonus >= 0 ? '+' : ''}${bonus}`
    quickRoll(notation, {
      note: `${ability} Check`,
      characterId,
      rollType: 'ability_check',
      abilityName: ability // Custom metadata
    })
  }

  function rollSavingThrow(saveName, characterId = null, bonus = 0) {
    const notation = `1d20${bonus >= 0 ? '+' : ''}${bonus}`
    quickRoll(notation, {
      note: `${saveName} Saving Throw`,
      characterId,
      rollType: 'saving_throw',
      saveName: saveName // Custom metadata
    })
  }

  function rollSkillCheck(skillName, characterId = null, bonus = 0) {
    const notation = `1d20${bonus >= 0 ? '+' : ''}${bonus}`
    quickRoll(notation, {
      note: `${skillName} Check`,
      characterId,
      rollType: 'skill_check',
      skillName: skillName // Custom metadata
    })
  }

  function rollInitiative(characterId = null, bonus = 0) {
    const notation = `1d20${bonus >= 0 ? '+' : ''}${bonus}`
    quickRoll(notation, {
      note: 'Initiative Roll',
      characterId,
      rollType: 'initiative'
    })
  }

  function rollDamage(damageNotation, characterId = null, damageType = 'Unknown') {
    quickRoll(damageNotation, {
      note: `Damage Roll (${damageType})`,
      characterId,
      rollType: 'damage',
      damageType: damageType // Custom metadata
    })
  } // API: open overlay with multiple dice groups at once (robust multi-dice init)
  function openWithGroups(groups = [], modifier = 0) {
    const normalized = (Array.isArray(groups) ? groups : [])
      .map((g) => ({
        type: typeof g.type === 'string' ? g.type : null,
        count: Math.max(1, parseInt(g.count, 10) || 1)
      }))
      .filter((g) => !!g.type)
    isVisible.value = true
    pendingConfiguration.value = {
      initialDiceGroups: normalized,
      ...(Number.isFinite(modifier) && modifier !== 0 ? { initialModifier: Number(modifier) } : {})
    }
    console.log(
      'DiceRollerStore: Stored pending configuration (groups):',
      pendingConfiguration.value
    )
  }

  // Export store
  return {
    // state
    isVisible,
    overlayOpacity,
    activeDiceSkin,
    diceColor,
    letterColor,
    use3DMode,
    playDiceSound,
    diceToRoll,
    rollHistory,
    activeRoll,
    availableSkins,
    allDiceTypes,
    // getters/derived
    isDiceBoxEmpty,
    totalDiceCount,
    getSkinByValue,
    getDiceAppearance,
    // config/pending
    pendingConfiguration,
    pendingModifier,
    pendingDiceType,
    pendingDiceCount,
    pendingDiceGroups,
    // actions
    showDiceRoller,
    openWithGroups,
    quickRoll,
    clearPendingConfiguration,
    hideDiceRoller,
    toggleDiceRoller,
    setDiceSkin,
    setOverlayOpacity,
    setUse3DMode,
    getUse3DMode,
    setPlayDiceSound,
    getPlayDiceSound,
    setDiceColor,
    setLetterColor,
    setRolling,
    addDice,
    clearDice,
    removeDieByIndex,
    addRollToHistory,
    clearRollHistory,
    addDiceFromNotation,
    performRoll
  }
})
